import { LandedCostComponents } from '@/helpers/landedCostDerivation';
import {
  supplierToStoreRate,
  hasUsableSupplierToStoreRate,
  landedCostForEnteredCost,
} from '@/views/purchase-order/poReceiveCosts';

/**
 * US-093 — cost arithmetic for the "Finalise costs" dialog.
 *
 * Same rule as receiving (US-092): what the merchant types is the SUPPLIER's
 * clean unit price in the SUPPLIER's (PO's) currency. The landed figure shown
 * beside it is derived, never entered, and never sent.
 *
 * Kept out of the component so the submit gate and the payload shape are
 * genuinely covered — those are the two places this can silently do the wrong
 * thing. The conversion helpers are reused from `poReceiveCosts.ts` unchanged
 * so there is exactly one implementation of the arithmetic.
 */

/** A price move smaller than this is not a correction. Mirrors the backend. */
export const PRICE_TOLERANCE = 0.005;

/** Anything above this is a typo, not a unit cost. */
export const MAX_UNIT_COST = 1000000;

export interface FinalizationLine {
  id?: number | string | null;
  unitPrice?: number | string | null;
  orderedQuantity?: number | string | null;
  /** Display only — never sent back. */
  productName?: string | null;
  /** Display only — never sent back. */
  variantTitle?: string | null;
  /** Display only — raw PO items carry the variant here instead of `variantTitle`. */
  variant?: string | null;
  /** Display only — never sent back. */
  sku?: string | null;
  /** Display only — never sent back. */
  supplierSku?: string | null;
  extra?: { landed_cost_components?: LandedCostComponents | null } | null;
}

/**
 * A line's SKU or Supplier SKU as the Finalise costs table shows it (AS-11291).
 * Supplier invoices rarely use the merchant's product names, so these columns
 * are what the merchant matches invoice lines on. Blank and the literal "null"
 * (a legacy artefact of stringified SKUs on older PO items) both read as empty.
 */
export function displayIdentifier(value: string | null | undefined): string {
  const text = value == null ? '' : String(value).trim();
  return text === 'null' ? '' : text;
}

/** True when at least one line has a value for this identifier, i.e. its column is worth showing. */
export function hasAnyIdentifier(
  items: FinalizationLine[] | null | undefined,
  key: 'sku' | 'supplierSku',
): boolean {
  if (!Array.isArray(items)) { return false; }
  return items.some((item) => displayIdentifier(item?.[key]) !== '');
}

/**
 * The variant name shown under the product name. Mapped lines carry
 * `variantTitle`; warbler passes raw PO items, which only carry `variant`.
 */
export function lineVariant(item: FinalizationLine | null | undefined): string {
  return item?.variantTitle || item?.variant || '';
}

export interface FinalizationFinancial {
  shipping_cost?: number | string | null;
  adjustment_amount?: number | string | null;
}

export type EnteredCosts = { [key: number]: number | string | null };

const num = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/** Prefill every line with its current unit price (AC3). */
export function buildInitialFinalizationCosts(
  items: FinalizationLine[] | null | undefined,
): { [key: number]: number } {
  const costs: { [key: number]: number } = {};
  if (!Array.isArray(items)) { return costs; }

  items.forEach((item) => {
    if (item?.id == null) { return; }
    costs[Number(item.id)] = num(item.unitPrice);
  });

  return costs;
}

/**
 * What Shopify would receive for an entered cost, or null when the PO carries no
 * usable exchange rate.
 *
 * Returning null rather than a number is the point: with an unusable rate the
 * conversion silently falls back to 1, which would label a supplier-currency
 * amount as store currency right next to "this is what Shopify gets".
 */
export function landedPreviewForLine(
  enteredCost: number | null | undefined,
  poCurrencyCode: string | null | undefined,
  storeCurrency: string | null | undefined,
  exchangeRate: number | null | undefined,
  components: LandedCostComponents | null | undefined,
): number | null {
  if (!hasUsableSupplierToStoreRate(poCurrencyCode, storeCurrency, exchangeRate)) { return null; }

  const rate = supplierToStoreRate(poCurrencyCode, storeCurrency, exchangeRate);
  return landedCostForEnteredCost(enteredCost, rate, components);
}

const isValidCost = (value: unknown): boolean => {
  // Number('') and Number(null) are both 0, and `v-model.number` writes '' when
  // the merchant CLEARS the field — so without this an emptied input would
  // validate and submit a zero unit cost, on to the supplier cost and Shopify.
  if (value === null || value === undefined) { return false; }
  if (typeof value === 'string' && value.trim() === '') { return false; }
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 && n <= MAX_UNIT_COST;
};

/** True when at least one line moved by at least the tolerance. */
export function hasAnyLineChange(
  items: FinalizationLine[] | null | undefined,
  entered: EnteredCosts | null | undefined,
): boolean {
  if (!Array.isArray(items) || !entered) { return false; }

  return items.some((item) => {
    if (item?.id == null) { return false; }
    const key = Number(item.id);
    if (entered[key] === undefined) { return false; }
    // A blank entry is 'not edited', never a move to zero.
    if (!isValidCost(entered[key])) { return false; }
    return Math.abs(num(entered[key]) - num(item.unitPrice)) >= PRICE_TOLERANCE;
  });
}

/** True when shipping or the adjustment moved. */
export function hasFinancialChange(
  financial: FinalizationFinancial | null | undefined,
  enteredFinancial: FinalizationFinancial | null | undefined,
): boolean {
  if (!enteredFinancial) { return false; }

  return (['shipping_cost', 'adjustment_amount'] as const).some((key) => {
    if (enteredFinancial[key] === undefined || enteredFinancial[key] === null) { return false; }
    return Math.abs(num(enteredFinancial[key]) - num(financial?.[key])) >= PRICE_TOLERANCE;
  });
}

/**
 * AC6. Submit needs a real change, and every entered cost must be valid.
 * Note what is deliberately NOT required: an invoice number or a reason (D15).
 */
export function canSubmitFinalization(params: {
  items?: FinalizationLine[] | null;
  entered?: EnteredCosts | null;
  financial?: FinalizationFinancial | null;
  enteredFinancial?: FinalizationFinancial | null;
  invoiceNumber?: string | null;
  reason?: string | null;
}): boolean {
  const { items, entered, financial, enteredFinancial } = params;

  const values = entered ? Object.values(entered) : [];
  if (values.some((value) => !isValidCost(value))) { return false; }

  return hasAnyLineChange(items, entered) || hasFinancialChange(financial, enteredFinancial);
}

/**
 * How the cost pushed to Shopify is derived. Same two values Receive Products
 * offers, so a merchant meets one vocabulary rather than two.
 */
export type CostCalculationMethod = 'latest' | 'average';

export interface FinalizationPayload {
  items: Array<{ purchaseOrderItemId: number; unitPrice: number }>;
  financial?: { shipping_cost: number; adjustment_amount: number };
  invoiceNumber?: string;
  reason?: string;
  notes?: string;
  updateShopifyCost?: boolean;
  costCalculationMethod?: CostCalculationMethod;
  updateSupplierCost?: boolean;
  markAsComplete?: boolean;
}

/**
 * Build the request body. Only lines that actually moved are sent, and each
 * carries exactly two keys — the backend rejects any quantity or `_action` key
 * outright, so sending one would fail the whole submit.
 */
export function buildFinalizationPayload(params: {
  items?: FinalizationLine[] | null;
  entered?: EnteredCosts | null;
  financial?: FinalizationFinancial | null;
  enteredFinancial?: FinalizationFinancial | null;
  invoiceNumber?: string | null;
  reason?: string | null;
  notes?: string | null;
  updateShopifyCost?: boolean;
  costCalculationMethod?: CostCalculationMethod;
  updateSupplierCost?: boolean;
  markAsComplete?: boolean;
}): FinalizationPayload {
  const {
    items, entered, financial, enteredFinancial,
    invoiceNumber, reason, notes,
    updateShopifyCost, costCalculationMethod, updateSupplierCost, markAsComplete,
  } = params;

  const changed: Array<{ purchaseOrderItemId: number; unitPrice: number }> = [];
  if (Array.isArray(items) && entered) {
    items.forEach((item) => {
      if (item?.id == null) { return; }
      const key = Number(item.id);
      if (entered[key] === undefined) { return; }
      if (!isValidCost(entered[key])) { return; }

      const value = num(entered[key]);
      if (Math.abs(value - num(item.unitPrice)) < PRICE_TOLERANCE) { return; }

      changed.push({ purchaseOrderItemId: key, unitPrice: value });
    });
  }

  const payload: FinalizationPayload = { items: changed };

  if (hasFinancialChange(financial, enteredFinancial)) {
    payload.financial = {
      shipping_cost: num(enteredFinancial?.shipping_cost),
      adjustment_amount: num(enteredFinancial?.adjustment_amount),
    };
  }

  if (invoiceNumber) { payload.invoiceNumber = invoiceNumber; }
  if (reason) { payload.reason = reason; }
  if (notes) { payload.notes = notes; }

  if (updateShopifyCost !== undefined) { payload.updateShopifyCost = updateShopifyCost; }
  // Only sent when the push is actually on — it has no meaning otherwise, and
  // omitting it lets the backend keep its own 'latest' default.
  if (updateShopifyCost && costCalculationMethod) {
    payload.costCalculationMethod = costCalculationMethod;
  }
  if (markAsComplete !== undefined) { payload.markAsComplete = markAsComplete; }
  // Omitted, not defaulted: absent means "no preference", which the backend
  // resolves against the merchant's stored setting. Sending false here would
  // silently override a merchant who opted in (US-092).
  if (updateSupplierCost !== undefined) { payload.updateSupplierCost = updateSupplierCost; }

  return payload;
}

/**
 * D8. Finalising sets the cost directly rather than re-averaging, so a PO whose
 * receives used `average` needs to say so on the form.
 */
export function usesAverageCostMethod(
  receives: Array<{ costCalculationMethod?: string | null }> | null | undefined,
): boolean {
  if (!Array.isArray(receives)) { return false; }
  return receives.some((receive) => receive?.costCalculationMethod === 'average');
}

/** One line's outcome, as the backend records it on the audit row. */
export interface CostSyncAppliedLine {
  inventoryItemId: string;
  sku?: string | null;
  productName?: string | null;
  variantTitle?: string | null;
  cost?: number;
  applied: CostCalculationMethod;
  reason?: string;
  onHand?: number;
  currentCost?: number;
}

/** `data.costSync` on a `cost_correction` history row. */
export interface CostSyncAudit {
  costUpdateStatus?: string;
  requested?: CostCalculationMethod;
  applied?: CostSyncAppliedLine[];
  blockedInventoryItemIds?: string[];
}

const METHOD_LABEL: Record<string, string> = {
  latest: 'latest cost',
  average: 'weighted average',
};

/**
 * One-line summary of what actually reached Shopify, for the PO history entry.
 *
 * Exists because the weighted average silently degrades to the latest cost when
 * a variant has no positive on-hand to blend against — correct, but invisible.
 * A merchant who picked Weighted Average and saw the latest cost land had no way
 * to tell why (AS-10622).
 */
export function describeCostSync(costSync?: CostSyncAudit | null): string {
  if (!costSync) { return ''; }

  const blocked = costSync.blockedInventoryItemIds?.length || 0;
  if (costSync.costUpdateStatus === 'blocked_by_inventory_sync') {
    return `Shopify cost blocked by inventory sync on ${blocked} ${blocked === 1 ? 'line' : 'lines'}`;
  }
  if (costSync.costUpdateStatus === 'failed') {
    return 'Shopify cost sync failed';
  }

  const applied = Array.isArray(costSync.applied) ? costSync.applied : [];
  if (applied.length === 0) { return ''; }

  const requested = costSync.requested || 'latest';
  const requestedLabel = METHOD_LABEL[requested] || requested;
  const fellBack = applied.filter((line) => line.applied !== requested);

  if (fellBack.length === 0) {
    return `Shopify cost: ${requestedLabel}`;
  }

  // Every distinct reason, so two lines skipped for different causes both show.
  const reasons = Array.from(new Set(fellBack.map((line) => line.reason).filter(Boolean)));
  const detail = reasons.length > 0 ? ` (${reasons.join('; ')})` : '';
  const noun = fellBack.length === 1 ? 'line' : 'lines';
  const fallbackLabel = METHOD_LABEL[fellBack[0].applied] || fellBack[0].applied;

  return `Shopify cost: ${requestedLabel} requested · ${fallbackLabel} used on `
    + `${fellBack.length} ${noun}${detail}`;
}

/**
 * How a repriced line is named in the history. Merchants read this, so an id is
 * the last resort, not the default: product + variant reads best, SKU is the
 * next most recognisable, and a bare id only appears for rows written before
 * the identity was recorded.
 *
 * Mirror of atlas's; warbler renders a single description line rather than a
 * row list, so only the naming rule is shared.
 */
export function describeLine(line: {
  sku?: string | null;
  productName?: string | null;
  variantTitle?: string | null;
  poItemId?: number | null;
  inventoryItemId?: string | null;
}): string {
  const name = [line.productName, line.variantTitle]
    .filter((part) => part && String(part).trim())
    .join(' \u00b7 ');

  if (name && line.sku) { return `${name} (${line.sku})`; }
  if (name) { return name; }
  if (line.sku) { return String(line.sku); }

  const id = line.poItemId ?? line.inventoryItemId;
  return id != null ? `Line ${id}` : 'Line';
}
